// Import the plugins
import Uppy from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import XHRUpload from '@uppy/xhr-upload';
import {defaults} from 'lodash-es';

// And their styles (for UI plugins)
import '@uppy/core/dist/style.css';
import '@uppy/dashboard/dist/style.css';

const FIELD_NAME = 'file';

/**
 * The server rejects anything the bytes do not back up, so the restrictions only save the staff
 * member a round trip. Reading them back keeps the numbers identical to the ones the validator
 * applies, and the same response carries the CSRF token the POST is checked against.
 */
export async function fetchUploadSettings(uploadUrl) {
  const response = await fetch(uploadUrl, {credentials: 'same-origin'});
  const body = await response.json();

  return {
    restrictions: {
      maxNumberOfFiles: 1,
      maxFileSize: body.maxFileSize,
      allowedFileTypes: body.allowedFileTypes,
    },
    csrfField: body.csrfField,
    csrfToken: body.csrfToken,
  };
}

function errorMessage(responseText) {
  try {
    return JSON.parse(responseText).error || 'This file could not be uploaded.';
  } catch (e) {
    return 'This file could not be uploaded.';
  }
}

export default function (selector, options) {
  const defaultOptions = {
    // This should be overridden by the client code. I'm putting a default just so we know
    // what the client code should look like
    onSuccessfulUpload(name, remoteUrl) {
      // eslint-disable-next-line no-console
      console.log(`${name} was uploaded successfully and can be found at ${remoteUrl}`);
    }
  };
  defaults(options, defaultOptions);

  const uppy = Uppy({restrictions: {maxNumberOfFiles: 1}})
    .use(Dashboard, {
      proudlyDisplayPoweredByUppy: false,
      trigger: selector,
      closeAfterFinish: true,
      note: 'Attach image, PDF, or contact card (VCF)',
      locale: {
        strings: {
          uploadXFiles: {
            0: 'Attach %{smart_count} file',
            1: 'Attach %{smart_count} files'
          },

        }
      }
    })
    .use(XHRUpload, {
      endpoint: options.uploadUrl,
      fieldName: FIELD_NAME,
      formData: true,
      withCredentials: true,
      getResponseError: (responseText) => new Error(errorMessage(responseText)),
    })
    .on('complete', (result) => {
      // Uppy fires complete even when every file was rejected; successful is then empty.
      if (!result.successful.length) {
        return;
      }

      options.onSuccessfulUpload(result.successful[0].name, result.successful[0].uploadURL);
    });

  fetchUploadSettings(options.uploadUrl)
    .then(({restrictions, csrfField, csrfToken}) => {
      uppy.setOptions({restrictions});
      if (csrfToken) {
        // XHRUpload puts every meta field in the multipart body, which is where the form reads it.
        uppy.setMeta({[csrfField]: csrfToken});
      }
    })
    // eslint-disable-next-line no-console
    .catch((error) => console.error('Could not read the upload settings', error));

  return uppy;
}
